/** Who reached this beneficiary — the booking wave, or a person.
 *
 * Asked for 2026-10-01: "how do I know this contact was made by operator
 * or the system". Extracted the moment it was wanted in a third place
 * (campaign list row, beneficiary drawer, contact detail page) — three
 * copies of a rule about what a BLANK means is three chances to let one
 * of them start guessing.
 *
 * The rule: blank is "reached before this was recorded", which is
 * neither answer. It renders nothing. Asserting "System" for a row we
 * cannot account for would be indistinguishable from a row we can.
 */
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/lib/format";

type ContactedFields = {
  contacted_by?: "system" | "operator" | "";
  last_contacted_source?: string;
  last_contacted_at?: string | null;
};

/** The two-bucket pill shown beside a status. */
export function ContactedByBadge({
  membership,
  testId,
}: {
  membership: ContactedFields;
  testId?: string;
}) {
  const { t } = useTranslation();
  const bucket = membership.contacted_by;
  if (!bucket) return null;
  return (
    <span
      className={`pill text-[10px] ${
        bucket === "operator" ? "text-nb-mint" : "text-nb-text-2"
      }`}
      // The precise source — the daily wave and an operator pressing
      // "Send wave now" both bucket differently but send the same
      // message, so the exact one is worth keeping within reach.
      title={t(`leads.contactSource.${membership.last_contacted_source}`, "")}
      data-testid={testId}
    >
      {t(`leads.contactedBy.${bucket}`)}
    </span>
  );
}

/** "Last contacted <when> — <precise source>", for the detail surfaces
 *  where there is room to say it in full. */
export function LastContactedLine({
  membership,
  testId,
  className = "text-xs text-nb-text-2",
}: {
  membership: ContactedFields;
  testId?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!membership.last_contacted_at) return null;
  return (
    <p className={className} data-testid={testId}>
      {t("leads.lastContacted")}:{" "}
      <span className="font-medium text-nb-text">
        {formatDateTime(membership.last_contacted_at)}
      </span>
      {membership.last_contacted_source && (
        <span className="text-nb-text-3">
          {" — "}
          {t(`leads.contactSource.${membership.last_contacted_source}`)}
        </span>
      )}
    </p>
  );
}
